import React, { useEffect, useState, useRef } from "react";
import { UilSync } from "@iconscout/react-unicons";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import { fetchPaymentsHistory } from "../../utils/SonicAPIV2";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/auth.context";

// Pagination bar component (reuse for orders, payments, etc.)
function Pagination({ page, setPage, pageSize, setPageSize, totalCount }) {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const firstItem = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastItem = Math.min(page * pageSize, totalCount);

  let pages = [];
  if (totalPages <= 7) {
    pages = Array.from({ length: totalPages }, (_, i) => i + 1);
  } else {
    pages = [1];
    if (page > 4) pages.push("...");
    for (
      let i = Math.max(2, page - 1);
      i <= Math.min(totalPages - 1, page + 1);
      i++
    ) {
      pages.push(i);
    }
    if (page < totalPages - 3) pages.push("...");
    pages.push(totalPages);
  }

  return (
    <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between w-full">
        <div>
          <p className="text-sm text-gray-700">
            Showing <span className="font-medium">{firstItem}</span> to{" "}
            <span className="font-medium">{lastItem}</span> of{" "}
            <span className="font-medium">{totalCount}</span> results
          </p>
        </div>
        <div className="flex items-center gap-4">
          <nav
            aria-label="Pagination"
            className="isolate inline-flex -space-x-px rounded-md shadow-xs"
          >
            <button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="sr-only">Previous</span>
              <ChevronLeftIcon aria-hidden="true" className="size-5" />
            </button>
            {pages.map((p, idx) =>
              p === "..." ? (
                <span
                  key={`ellipsis-${idx}`}
                  className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-gray-300 focus:outline-offset-0 select-none"
                >
                  ...
                </span>
              ) : (
                <button
                  key={p}
                  aria-current={p === page ? "page" : undefined}
                  className={
                    p === page
                      ? "relative z-10 inline-flex items-center bg-indigo-600 px-4 py-2 text-sm font-semibold text-white focus:z-20 ring-1 ring-indigo-600"
                      : "relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                  }
                  onClick={() => setPage(p)}
                  disabled={p === page}
                >
                  {p}
                </button>
              )
            )}
            <button
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
              className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="sr-only">Next</span>
              <ChevronRightIcon aria-hidden="true" className="size-5" />
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
}

export const UserPayments = () => {
  const [payments, setPayments] = useState([]);
  const [paymentsCount, setPaymentsCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Paging
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    RefreshData();
    // eslint-disable-next-line
  }, []);

  async function RefreshData() {
    setLoading(true);
    setError(null);
    try {
      // ✅ FIXED: Pass object with named parameters
      const data = await fetchPaymentsHistory({ summary: false });

      // console.log("Payments data from UserPayments.jsx:", data);

      // ✅ Extract paymentList and paymentCount (not lastPayments)
      const list = data?.paymentList || [];
      setPayments(list);
      setPaymentsCount(data?.paymentCount || list.length);
      setPage(1); // Reset to first page
    } catch (error) {
      // console.error("Error loading payments:", error);
      setError(error.message || "Failed to load payments");
    } finally {
      setLoading(false);
    }
  }

  // Format date
  function formatDate(dateString) {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-AE", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return dateString;
    }
  }

  // Pagination logic
  const totalCount = payments.length;
  const pagedPayments = payments.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="mx-auto mb-28">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="sm:flex sm:items-center">
          <div className="text-navarbarText text-3xl font-poppins font-medium mb-8">
            Payment List
            <p className="text-base text-darkTextColor font-poppins font-normal">
              List of all Payments with Amount
            </p>
          </div>
          <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
            <button
              className="flex rounded-md bg-secondary h-10 w-10 items-center justify-center text-center hover:bg-secondaryHover disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={RefreshData}
              disabled={loading}
            >
              <UilSync
                className={`w-6 text-textWhite ${
                  loading ? "animate-spin" : ""
                }`}
              />
            </button>
          </div>
        </div>
        <div className="-mx-4 mt-8 sm:-mx-0">
          <table className="min-w-full divide-y divide-gray-300">
            <thead>
              <tr>
                <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">
                  Payment #
                </th>
                <th className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell">
                  Date
                </th>
                <th className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 lg:table-cell">
                  Invoice #
                </th>
                <th className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 lg:table-cell">
                  Payment Type
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading ? (
                <tr>
                  <td colSpan="5" className="py-4 text-center">
                    Loading...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="5" className="py-4 text-center text-red-600">
                    {error}
                  </td>
                </tr>
              ) : pagedPayments.length > 0 ? (
                pagedPayments.map((payment) => (
                  <tr key={payment.paymentNumber}>
                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0">
                      {payment.paymentNumber}
                    </td>
                    <td className="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell">
                      {formatDate(payment.paymentDate)}
                    </td>
                    <td className="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 lg:table-cell">
                      {payment.invoiceNumber || "N/A"}
                    </td>
                    <td className="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 lg:table-cell">
                      {payment.paymentType}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {Number(payment.paidAmt ?? 0).toLocaleString("en-AE", {
                        style: "currency",
                        currency: "AED",
                        minimumFractionDigits: 2,
                      })}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="5"
                    className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0 text-center"
                  >
                    No record found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Paging bar */}
        <Pagination
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalCount={totalCount}
        />
      </div>
    </div>
  );
};
